import { Carousel } from "hec/components/ui/carousel";
import type * as React from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";

import { cn } from "~/lib/utils";

import {
  defaultCarouselContent,
  defaultContentNav,
  defaultNextButton,
  defaultPreviousButton,
} from "./defaultConfig";

interface CustomCarouselProps {
  children?: React.ReactNode;
  loop?: boolean;
  slidesToScroll?: number;
  align?: "start" | "center" | "end";
  autoplay?: boolean;
  autoplayDelay?: number;
  stopOnInteraction?: boolean;
  stopOnMouseEnter?: boolean;
  attributes?: Record<string, unknown> & { className?: string };
}

export const CustomCarousel = (props: CustomCarouselProps) => {
  const {
    children,
    loop,
    slidesToScroll,
    align,
    autoplay,
    autoplayDelay,
    stopOnInteraction,
    stopOnMouseEnter,
    attributes,
  } = props;
  const { className, ...restAttributes } = attributes || {};
  return (
    <Carousel
      opts={{ loop, slidesToScroll, align }}
      autoplay={autoplay}
      autoplayDelay={autoplayDelay}
      stopOnInteraction={stopOnInteraction}
      stopOnMouseEnter={stopOnMouseEnter}
      className={cn("mx-auto w-[80%]", className)}
      {...restAttributes}
      data-testid="carousel-Carousel"
    >
      {children}
    </Carousel>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const CarouselDefinition: RegisteredComponent = {
  component: CustomCarousel,
  name: "v1: Carousel",
  canHaveChildren: true,
  childRequirements: {
    message:
      "Children of 'Carousel' must be 'CarouselButton', 'CarouselContentNav', or 'v1: CarouselContent'",
    query: {
      "component.name": {
        $in: ["CarouselButton", "v1: CarouselContent", "CarouselContentNav"],
      },
    },
  },
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  defaultStyles: {
    marginLeft: "auto",
    marginRight: "auto",
    width: "80%",
  },
  defaultChildren: [
    defaultPreviousButton,
    defaultCarouselContent,
    defaultNextButton,
    defaultContentNav,
  ] as unknown as RegisteredComponent["defaultChildren"],
  inputs: [
    {
      name: "align",
      type: "string",
      enum: ["start", "center", "end"],
      defaultValue: "start",
      helperText: "Align the slides relative to the carousel viewport.",
    },
    {
      name: "loop",
      type: "boolean",
      defaultValue: true,
      helperText: "Enables infinite looping.",
    },
    {
      name: "slidesToScroll",
      type: "number",
      defaultValue: 1,
      helperText: "Group slides together.",
    },
    {
      name: "autoplay",
      type: "boolean",
      defaultValue: false,
      helperText: "Automatically advance slides.",
    },
    {
      name: "autoplayDelay",
      type: "number",
      defaultValue: 9000,
      helperText: "Delay between slide changes (ms).",
    },
    {
      name: "stopOnInteraction",
      type: "boolean",
      defaultValue: true,
      helperText: "Stop autoplay after user interaction.",
    },
    {
      name: "stopOnMouseEnter",
      type: "boolean",
      defaultValue: false,
      helperText: "Pause autoplay while hovering.",
    },
  ],
};
